// Adapted from example at https://material-ui.com/components/autocomplete/#google-maps-place

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Autocomplete from '@mui/material/Autocomplete';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import * as React from 'react';
import { useGoogleMapsApi } from 'hooks/useGoogleMapsApi';

const autocompleteService = { current: null };

interface PlaceType {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings: [
      {
        offset: number;
        length: number;
      }
    ];
  };
}

interface OwnProps {
  renderInput: (params: any) => React.ReactNode;
  onOptionSelected: (selectedOption: PlaceType) => void;
  value: string;
}

type Props = OwnProps;

export const GoogleMapsPlaceInput = ({ onOptionSelected, renderInput, value }: Props) => {
  const [options, setOptions] = React.useState<PlaceType[]>([]);

  const { isLoaded } = useGoogleMapsApi();

  const fetch = React.useMemo(
    () =>
      throttle((input: any, callback: any) => {
        (autocompleteService.current as any).getPlacePredictions(input, callback);
      }, 200),
    []
  );

  React.useEffect(() => {
    if (!isLoaded) {
      return undefined;
    }

    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (value === '') {
      setOptions([]);
      return undefined;
    }

    fetch({ input: value }, (results?: PlaceType[]) => {
      if (active) {
        setOptions(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [value, fetch, isLoaded]);

  return (
    <Autocomplete
      id="google-map-demo"
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.structured_formatting.main_text
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete={true}
      includeInputInList={true}
      freeSolo={true}
      openOnFocus={false}
      onChange={(event: any, newValue: any) => {
        onOptionSelected(newValue);
      }}
      renderInput={renderInput}
      value={value as any}
      renderOption={(props, option: any) => {
        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches?.map((match: any) => [match.offset, match.offset + match.length]) ?? []
        );

        return (
          <li {...props}>
            <Grid container={true} alignItems="center">
              <Grid item={true}>
                <LocationOnIcon
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    marginRight: 2,
                  }}
                />
              </Grid>
              <Grid item={true} xs={true}>
                {parts.map((part, index) => (
                  <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                    {part.text}
                  </span>
                ))}
                <Typography variant="body2" color="textSecondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};
